import { Box, Typography } from '@mui/material';
import { FC, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { ReactComponent as FailureIconVs } from 'src/assets/svg/failure-red-circle.svg';
import { ReactComponent as FailureIconHos } from 'src/assets/svg/failure-red-circle-hos.svg';
import { Button } from 'src/components/button';
import { ErrorReason } from 'src/shared/enum/BookingStatus';
import { getTheme } from 'src/utils/getTheme';

import { styles } from './styles';

const { isHosTheme } = getTheme();

const FailureIcon: FC = () => (isHosTheme ? <FailureIconHos /> : <FailureIconVs />);

const TextWrapper: FC<{ children: ReactNode }> = (props) => {
    const Wrapper = isHosTheme ? Box : Fragment;

    return <Wrapper>{props.children}</Wrapper>;
};

export const FailurePage: FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const failureReason = searchParams.get('reason');

    const bookingFailed = failureReason === ErrorReason.BOOKING_FAILED;
    const paymentFailed = failureReason === ErrorReason.PAYMENT_FAILED;
    const bookingNoPaymentFailed = failureReason === ErrorReason.BOOKING_WITHOUT_PAYMENT_FAILED;
    const reservationExpired = failureReason === ErrorReason.RESERVATION_EXPIRED;

    let title = '';
    let message = '';
    let buttonText = '';
    let ctaLink: string | (() => void) = '';

    switch (true) {
        case bookingFailed:
            title = t('booking_failure_title');
            message = t('failure_booking_payment_completed');
            break;

        case paymentFailed:
            title = t('payment_failure_title');
            message = t('payment_failure_content');
            break;

        case bookingNoPaymentFailed:
            title = t('booking_failure_title');
            message = t('failure_content');
            buttonText = t('failure_btn_no_advanced_fee');
            ctaLink = '/summary';
            break;

        case reservationExpired:
            title = t('reservation_expired_title');
            message = t('reservation_expired_message');
            break;

        default:
            title = t('failure_generic_title');
            message = t('failure_generic_message');
            buttonText = t('failure_generic_button');
            ctaLink = () => navigate(-1);
    }

    return (
        <Box css={styles.container}>
            <Box css={styles.imageContainer} />

            <Box css={styles.wrapper}>
                <FailureIcon />
                <TextWrapper>
                    <Typography variant="h1" css={styles.title}>
                        {title}
                    </Typography>
                    <Typography
                        variant="body3"
                        css={styles.link}
                        dangerouslySetInnerHTML={{ __html: message }}
                    />
                </TextWrapper>
                {!bookingFailed && !paymentFailed && !reservationExpired && (
                    <Button
                        {...(isHosTheme && { design: 'light', bordered: true })}
                        css={styles.cta}
                        onClick={typeof ctaLink === 'function' ? ctaLink : undefined}
                        href={typeof ctaLink === 'string' ? ctaLink : undefined}
                    >
                        {buttonText}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
